import { Stack, StackProps } from "@chakra-ui/react"
import { LiveStats } from "component/LiveStats"
import { Stats } from "component/Stats"
import { useTranslation } from "react-i18next"
import { useMarket } from "sdk-react/markets/useMarket"

import { MarketStatisticsContainer } from "../../container/MarketStatisticsContainer"

interface MarketStatsPrimaryProps extends StackProps {
    tickerSymbol: string
}

export function MarketStatsPrimary({ tickerSymbol }: MarketStatsPrimaryProps) {
    const { marketPrice } = useMarket(tickerSymbol)
    const { priceChangeRate24hStr } = MarketStatisticsContainer.useContainer()

    return (
        <LiveStats
            key={tickerSymbol + "market_price"}
            number={marketPrice?.toNumber() || 0}
            label={priceChangeRate24hStr}
            numberProps={{ lineHeight: 1 }}
            labelProps={{ opacity: 1 }}
            labelColor={priceChangeRate24hStr.startsWith("-") ? "red.400" : "green.300"}
            size={"md"}
            variant="bottom"
        />
    )
}

interface MarketStatsSecondaryProps extends StackProps {
    tickerSymbol: string
    isInline?: boolean
}

export function MarketStatsSecondary({ tickerSymbol, isInline }: MarketStatsSecondaryProps) {
    const { indexPrice, markPrice } = useMarket(tickerSymbol)
    const { volume24hStr, fundingRate8hStr } = MarketStatisticsContainer.useContainer()

    const { t } = useTranslation()

    const size = isInline ? "md" : "xs"
    const variant = isInline ? "top" : "left"

    return (
        <Stack spacing={isInline ? 6 : 2} isInline={isInline} flexShrink={0} shouldWrapChildren>
            <LiveStats
                key={tickerSymbol + "mark_price"}
                label={t("market.mark_price")}
                number={markPrice?.toNumber() || 0}
            />
            <LiveStats
                key={tickerSymbol + "index_price"}
                label={t("market.index_price")}
                number={indexPrice?.toNumber() || 0}
            />
            <Stats variant={variant} size={size} label="Volume (24h)" number={volume24hStr} />
            <Stats
                variant={variant}
                size={size}
                showTooltip
                label="Funding Rate (8h)"
                helper="The current realtime funding rate. Longs pay shorts if positive, shorts pay longs if negative."
                number={fundingRate8hStr}
            />
        </Stack>
    )
}
